import moment from 'moment';
import 'moment-timezone';
import { FALLBACK_DASH } from '../consts';

/**
 * Moment to short locale appropriate date string format
 * Use ONLY where horizontal space is minimal. Column headers in dense tables. Graph labels.
 *
 * @param  {moment} date The moment to format
 * @return {string} Date Output e.g. 02/10/2021
 */
export const formatShort = (date: moment.Moment): string => {
  if (!date || !date.isValid()) {
    return '';
  }
  return date.format('L');
};

/**
 * Moment to a locale appropriate time string format of a 12-hour clock with the appropriate AM/PM suffix
 * @param  {moment} date The moment to format
 * @return {string} Time Output e.g. 01:01 AM or 01:01 PM
 */

export const formatJustTimeWithAMPM = (date: moment.Moment): string =>
  date.format('hh:mm A');

/**
 * Function that returns the correct string based on the count if its plural (i.e. day or days)
 * @param  {number} count The number of minutes/hours/days/seconds
 * @param  {string} text The text that gets mutated to be plural if the count is higher than 1
 * @return {string} Text output e.g. day or days/hour or hours/minute or minutes
 */
export const getTimeText = (count: number, text: string): string => {
  return count > 1 ? `${text}s` : text;
};

/**
 * Function that returns the correct string of the time between now and the previous time stamp of the last update
 * @param  {string} prevTimestamp The last updated timestamp provided
 * @return {string} Text output of the days hours and minutes since the last update occurred.
 *  e.g. Last updated 24 days 16 hours 20 minutes ago
 */
export const getLastUpdatedTimeMinDiff = (prevTimestamp: string) => {
  const currentTimestamp = new Date();
  const lastUpdatedInMinutes = Math.floor(
    (currentTimestamp.getTime() - new Date(prevTimestamp).getTime()) / 60000
  );
  const days = Math.floor(lastUpdatedInMinutes / 24 / 60);
  const hours = Math.floor((lastUpdatedInMinutes / 60) % 24);
  const minutes = Math.floor(lastUpdatedInMinutes % 60);

  const dayString = days ? `${days} ${getTimeText(days, 'day')} ` : '';
  const hourString = hours ? `${hours} ${getTimeText(hours, 'hour')} ` : '';
  const minuteString = minutes
    ? `${minutes} ${getTimeText(minutes, 'minute')} `
    : '';

  const timePastText =
    dayString || hourString || minuteString
      ? `${dayString}${hourString}${minuteString}`
      : 'less than a minute ';

  return `Last updated ${timePastText}ago`;
};

/**
 * Generic function to format an ISO date string in a specific timezone
 * If both isoString and timezone are missing, returns the fallback value
 * If timezone is missing or invalid, defaults to UTC timezone
 * Customizable format and fallback value for maximum reusability
 *
 * @param isoString - ISO 8601 date string (e.g., "2026-04-15T19:00:00Z")
 * @param timezone - IANA timezone string (e.g., "America/New_York"). If not provided, uses UTC
 * @param format - Moment format string (default: "DD MMM YYYY")
 * @param fallback - Fallback string for invalid dates (default: FALLBACK_DASH)
 * @return {string} Formatted date output or fallback if invalid
 *
 * @example
 * formatDateInTimezone('2026-04-15T19:00:00Z', 'America/New_York')
 * // Returns: "15 Apr 2026"
 *
 * @example
 * formatDateInTimezone('2026-04-15T19:00:00Z', '', 'YYYY-MM-DD')
 * // Returns: "2026-04-15" (in UTC)
 *
 * @example
 * formatDateInTimezone('invalid', 'UTC', 'DD MMM YYYY', 'N/A')
 * // Returns: "N/A"
 */
export const formatDateInTimezone = (
  isoString: string,
  timezone: string,
  format: string = 'DD MMM YYYY',
  fallback: string = FALLBACK_DASH
): string => {
  if (!isoString && !timezone) {
    return fallback;
  }

  if (!timezone){
    return moment.utc(isoString).format(format);
  }

  // Validate timezone exists in moment-timezone database
  const isValidTimezone = timezone && moment.tz.zone(timezone) !== null;

  // Parse with timezone if valid, otherwise fallback to UTC explicitly
  const dateMoment = isValidTimezone
    ? moment.tz(isoString, timezone)
    : moment.tz(isoString, 'UTC');

  // Return fallback if date is invalid
  if (!dateMoment.isValid()) {
    return fallback;
  }

  // Format with provided or default format string
  return dateMoment.format(format);
};

/**
 * Generic function to format an ISO time string in a specific timezone with timezone abbreviation
 * If both isoString and timezone are missing, returns the fallback value
 * If timezone is missing or invalid, defaults to UTC timezone
 * Customizable format and fallback value for maximum reusability
 *
 * @param isoString - ISO 8601 date string (e.g., "2026-04-15T19:00:00Z")
 * @param timezone - IANA timezone string (e.g., "America/New_York"). If not provided, uses UTC
 * @param format - Moment format string (default: "h:mm A z" - includes timezone abbreviation)
 * @param fallback - Fallback string for invalid times (default: FALLBACK_DASH)
 * @return {string} Formatted time output or fallback if invalid
 *
 * @example
 * formatTimeInTimezone('2026-04-15T19:00:00Z', 'America/New_York')
 * // Returns: "3:00 PM EDT"
 *
 * @example
 * formatTimeInTimezone('2026-04-15T19:00:00Z', '', 'HH:mm')
 * // Returns: "19:00" (in UTC)
 *
 * @example
 * formatTimeInTimezone('invalid', 'UTC', 'h:mm A z', 'N/A')
 * // Returns: "N/A"
 */
export const formatTimeInTimezone = (
  isoString: string,
  timezone: string,
  format: string = 'h:mm A z',
  fallback: string = FALLBACK_DASH
): string => {
  if (!isoString && !timezone) {
    return fallback;
  }

  if (!timezone){
    return moment.utc(isoString).format(format);
  }

  // Validate timezone exists in moment-timezone database
  const isValidTimezone = timezone && moment.tz.zone(timezone) !== null;

  // Parse with timezone if valid, otherwise fallback to UTC explicitly
  const timeMoment = isValidTimezone
    ? moment.tz(isoString, timezone)
    : moment.tz(isoString, 'UTC');

  // Return fallback if time is invalid
  if (!timeMoment.isValid()) {
    return fallback;
  }

  // Format with provided or default format string
  return timeMoment.format(format);
};